// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  User,
  UserIdentifier,
  getOrganizationUsers,
} from '@/account/api/get-organization-users';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { PersonAutocomplete } from '@/components/person-autocomplete';
import { useQuery } from '@tanstack/react-query';
import { ForwardedRef, forwardRef } from 'react';

/**
 * Sorts an array of User objects alphabetically by their full names.
 *
 * @param {User[]} users - An array of User objects to be sorted.
 * @return {User[]} A new array of User objects sorted by firstName and lastName in ascending order.
 */
function getOptions(users: User[]): User[] {
  return users.sort((user, other) => {
    const name1 = user.firstName + ' ' + user.lastName;
    const name2 = other.firstName + ' ' + other.lastName;
    return name1.localeCompare(name2);
  });
}

interface Props {
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * The currently selected person.
   */
  value: UserIdentifier | null;

  /**
   * If true, the component will include deactivated users in the search results.
   */
  includeDeactivated?: boolean;

  /**
   * A callback fired when the selected person changes.
   */
  onChange: (value: User | null) => void;

  /**
   * If provided, the placeholder text will be displayed in the input element.
   */
  placeholder?: string;
}

/**
 * Renders a person autocomplete component with options from the remote API.
 * This function also forwards a ref to the autocomplete component.
 */
function PersonSearchRenderer(
  props: Props,
  ref: ForwardedRef<HTMLInputElement>
) {
  const { user } = useCurrentUser();
  const usersQuery = useQuery({
    queryKey: ['organization-users', user.organization.key],
    queryFn: () => getOrganizationUsers(user.organization.key),
  });

  const users =
    usersQuery.data?.users.filter((user: User) =>
      props.includeDeactivated ? true : !user.isDeactivated
    ) ?? [];

  const handleChange = (_e: unknown, value: User | null) => {
    props.onChange(value);
  };

  // Find the selected user from the options
  const selectedUser = props.value?.id
    ? (users.find((user) => user.id === props.value?.id) ?? null)
    : null;

  return (
    <PersonAutocomplete
      ref={ref}
      size="small"
      name={props.name}
      value={selectedUser}
      error={props.error}
      label={props.label}
      options={getOptions(users)}
      loading={usersQuery.isLoading}
      onChange={handleChange}
      helperText={props.helperText}
      placeholder={props.placeholder ? props.placeholder : 'Select a person'}
      loadingText="Loading..."
      // Add these props to ensure consistent behavior
      noOptionsText={usersQuery.isLoading ? 'Loading...' : 'No options'}
      // Disable the input while loading to prevent state changes
      disabled={usersQuery.isLoading}
    />
  );
}

/**
 * A controlled component for selecting a person from a list of options from
 * the remote API.
 */
export const PersonSearch = forwardRef(PersonSearchRenderer);
